import { iBidType, MUNICIPALITY_TAX_INCLUSIVE_STATUSES, VAT_INCLUSIVE_STATUSES } from "../types/eventTypes";
import { BUTTON_VARIANT } from "../types/coreButtonTypes";
import CanapesIcon from "../assets/svg-fc/CanapesIcon";
import StartersIcon from "../assets/svg-fc/StartersIcon";
import SaladsSoupsIcon from "../assets/svg-fc/SaladsSoupsIcon";
import MainCourseIcon from "../assets/svg-fc/MainCourseIcon";
import LiveStationIcon from "../assets/svg-fc/LiveStationIcon";
import SidesIcon from "../assets/svg-fc/SidesIcon";
import DrinksIcon from "../assets/svg-fc/DrinksIcon";
import DessertsIcon from "../assets/svg-fc/DessertsIcon";
import CustomIcon from "../assets/svg-fc/CustomIcon";
import {
	ICreateQuoteFormSection,
	IMenuCategory,
	IQuoteCalculatedDetails,
	IQuoteFormFinancialDetails,
	IQuoteFormMenuAndServicesInfo,
} from "../types/quoteFormTypes";
import { slateToHtml } from "@slate-serializers/html";
import { SlateToDomConfig } from "@slate-serializers/dom";
import { Config as HtmlToSlateConfig } from "@slate-serializers/html/src/lib/serializers/htmlToSlate/config/types";
import { IQuoteDetailsFormErrorsState, IQuoteDetailsFormTouchedState } from "../pages/QuoteForm/QuoteForm";
import { generateQuotePreviewHTML } from "./quotePreviewHelpers";
import { IAttachmentFile, ICorporateEventBid } from "../interfaces/corporateEvent";
import { iUserDetails } from "../types/userType";
import { addCommasInAmount } from "./commonUtils";
import { getFileNameFromUrl } from "../helpers/common";

// Custom configuration for slateToHtml to handle list elements
const slateToHtmlConfig: SlateToDomConfig = {
	elementMap: {
		p: "p",
		paragraph: "p",
		ul: "ul",
		ol: "ol",
		li: "li",
		// Map custom element types to HTML tags
		"bulleted-list": "ul",
		"numbered-list": "ol",
		"list-item": "li",
	},
	markMap: {
		strikethrough: ["s"],
		bold: ["strong"],
		underline: ["u"],
		italic: ["i"],
	},
	elementAttributeTransform: ({ node }) => {
		if (node.align) {
			return {
				style: `text-align: ${node.align}`,
			};
		}
		return;
	},
	elementTransforms: {},
	encodeEntities: false,
	alwaysEncodeBreakingEntities: true,
	alwaysEncodeCodeEntities: false,
	convertLineBreakToBr: false,
};

// Custom configuration for htmlToSlate to handle list elements
export const htmlToSlateConfig: HtmlToSlateConfig = {
	elementTags: {
		p: () => ({ type: "paragraph" }),
		ul: () => ({ type: "bulleted-list" }),
		ol: () => ({ type: "numbered-list" }),
		li: () => ({ type: "list-item" }),
	},
	textTags: {
		s: () => ({ strikethrough: true }),
		strong: () => ({ bold: true }),
		u: () => ({ underline: true }),
		i: () => ({ italic: true }),
	},
	elementAttributeTransform: ({ el }) => {
		const style = el.attribs?.style;
		if (style && style.includes("text-align")) {
			const align = style.match(/text-align:\s*(\w+)/)?.[1];
			if (align) {
				return { align };
			}
		}
		return;
	},
	filterWhitespaceNodes: true,
	convertBrToLineBreak: false,
	trimWhiteSpace: true,
};

export const vatInclusiveAmountData = [
	{
		buttonText: "Yes",
		value: VAT_INCLUSIVE_STATUSES.Yes,
		variant: BUTTON_VARIANT.OUTLINED,
		fullWidth: false,
		disabled: false,
		coreBtnPill: false,
	},
	{
		buttonText: "No",
		value: VAT_INCLUSIVE_STATUSES.No,
		variant: BUTTON_VARIANT.OUTLINED,
		fullWidth: false,
		disabled: false,
		coreBtnPill: false,
	},
	{
		buttonText: "I'm not VAT registered",
		value: VAT_INCLUSIVE_STATUSES.NotRegistered,
		variant: BUTTON_VARIANT.OUTLINED,
		fullWidth: false,
		disabled: false,
		coreBtnPill: false,
	},
];

export const municipalityTaxAmountData = [
	{
		buttonText: "Yes",
		variant: BUTTON_VARIANT.OUTLINED,
		value: MUNICIPALITY_TAX_INCLUSIVE_STATUSES.Yes,
		fullWidth: false,
		disabled: false,
		coreBtnPill: false,
	},
	{
		buttonText: "No",
		value: MUNICIPALITY_TAX_INCLUSIVE_STATUSES.No,
		variant: BUTTON_VARIANT.OUTLINED,
		fullWidth: false,
		disabled: false,
		coreBtnPill: false,
	},
	{
		buttonText: "Not applicable",
		value: MUNICIPALITY_TAX_INCLUSIVE_STATUSES.NotApplicable,
		variant: BUTTON_VARIANT.OUTLINED,
		fullWidth: false,
		disabled: false,
		coreBtnPill: false,
	},
];

export const menuCategories: IMenuCategory[] = [
	{ id: "canapes", name: "Canapes", icon: CanapesIcon },
	{ id: "starters", name: "Starters", icon: StartersIcon },
	{ id: "salads-soups", name: "Salads/Soups", icon: SaladsSoupsIcon },
	{ id: "main-course", name: "Main Course", icon: MainCourseIcon },
	{ id: "live-station", name: "Live Station", icon: LiveStationIcon },
	{ id: "sides", name: "Sides", icon: SidesIcon },
	{ id: "drinks", name: "Drinks", icon: DrinksIcon },
	{ id: "desserts", name: "Desserts", icon: DessertsIcon },
	{ id: "custom", name: "Custom", icon: CustomIcon },
];

export const serializeSectionsContent = (
	menuFormData: IQuoteFormMenuAndServicesInfo,
	servicesInfoSection: ICreateQuoteFormSection | null,
) => {
	return {
		...menuFormData,
		sections: [...menuFormData?.sections, servicesInfoSection as ICreateQuoteFormSection].map((section) => {
			const { clientId: _clientId, ...sectionData } = section ?? {};
			return {
				...sectionData,
				attachments: section?.attachments?.map((attachment, index) => {
					const title = attachment?.fileName?.trim();
					return {
						...attachment,
						fileName: title ? title : getFileNameFromUrl(attachment?.fileUrl, index),
					};
				}),
				content: slateToHtml(section?.content ? JSON.parse(section?.content) : [], slateToHtmlConfig),
			};
		}),
	};
};

export const isQuoteFormEmpty = (
	financialFormData: IQuoteFormFinancialDetails,
	menuFormData: IQuoteFormMenuAndServicesInfo,
) => {
	return (
		!financialFormData?.amount &&
		!financialFormData?.isVatInclusive &&
		!financialFormData?.isMunicipalityTaxInclusive &&
		!financialFormData?.municipalityTax &&
		!menuFormData?.openAiFileId &&
		!menuFormData?.aiBidsExtractionLogId &&
		!menuFormData?.quoteUrl &&
		!menuFormData?.sections?.length
	);
};

export const isQuoteDetailsFormInvalid = (
	touchedFields: IQuoteDetailsFormTouchedState,
	formErrors: IQuoteDetailsFormErrorsState,
	financialFormData: IQuoteFormFinancialDetails,
) => {
	let isFormValid = true;
	Object.values(formErrors).forEach((val) => {
		if (val !== "") isFormValid = false;
	});
	return !isFormValid || !touchedFields["amount"] || calculateQuoteDetails(financialFormData)?.total < 400;
};

export const isMenuAndServicesFormInvalid = (
	menuData: IQuoteFormMenuAndServicesInfo,
	servicesInfo: ICreateQuoteFormSection | null,
) => {
	// Check if menu sections have content
	const hasMenuWithContent =
		menuData?.sections?.length > 0 &&
		menuData.sections.every((section) => !checkIfRichTextIsEmpty(section?.content));

	// Check if service section has content
	const hasServiceWithContent = servicesInfo && !checkIfRichTextIsEmpty(servicesInfo?.content);

	return !hasMenuWithContent || !hasServiceWithContent;
};

export const calculateQuoteDetails = (
	financialFormData: IQuoteFormFinancialDetails,
): IQuoteCalculatedDetails => {
	let quoteAmount = isNaN(financialFormData.amount as number) ? 0 : Number(financialFormData.amount);
	let municipalityTax = isNaN(financialFormData.municipalityTax as number)
		? 0
		: Number(financialFormData.municipalityTax);
	let vat = 0.0;
	let subtotal = 0.0;
	let total = 0;

	if (financialFormData.isMunicipalityTaxInclusive === MUNICIPALITY_TAX_INCLUSIVE_STATUSES.NotApplicable) {
		if (financialFormData.isVatInclusive === VAT_INCLUSIVE_STATUSES.Yes) {
			vat = quoteAmount - Number((quoteAmount / 1.05).toFixed(2));
			subtotal = quoteAmount - vat;
		}

		if (financialFormData.isVatInclusive === VAT_INCLUSIVE_STATUSES.No) {
			vat = quoteAmount * (5 / 100);
			subtotal = quoteAmount;
		}

		if (financialFormData.isVatInclusive === VAT_INCLUSIVE_STATUSES.NotRegistered) {
			vat = quoteAmount * (5 / 100);
			subtotal = quoteAmount;
		}
		total = subtotal + vat;
	}

	if (financialFormData.isMunicipalityTaxInclusive === MUNICIPALITY_TAX_INCLUSIVE_STATUSES.Yes) {
		if (financialFormData.isVatInclusive === VAT_INCLUSIVE_STATUSES.Yes) {
			vat = quoteAmount - municipalityTax - (quoteAmount - municipalityTax) / 1.05;
			subtotal = quoteAmount - municipalityTax - vat;
		}

		if (financialFormData.isVatInclusive === VAT_INCLUSIVE_STATUSES.No) {
			vat = (quoteAmount - municipalityTax) * (5 / 100);
			subtotal = quoteAmount - municipalityTax;
		}

		if (financialFormData.isVatInclusive === VAT_INCLUSIVE_STATUSES.NotRegistered) {
			vat = (quoteAmount - municipalityTax) * (5 / 100);
			subtotal = quoteAmount - municipalityTax;
		}
		total = subtotal + municipalityTax + vat;
	}

	if (financialFormData.isMunicipalityTaxInclusive === MUNICIPALITY_TAX_INCLUSIVE_STATUSES.No) {
		if (financialFormData.isVatInclusive === VAT_INCLUSIVE_STATUSES.Yes) {
			vat = quoteAmount - quoteAmount / 1.05;
			subtotal = quoteAmount - vat;
		}

		if (financialFormData.isVatInclusive === VAT_INCLUSIVE_STATUSES.No) {
			vat = quoteAmount * (5 / 100);
			subtotal = quoteAmount;
		}

		if (financialFormData.isVatInclusive === VAT_INCLUSIVE_STATUSES.NotRegistered) {
			vat = quoteAmount * (5 / 100);
			subtotal = quoteAmount;
		}
		total = subtotal + municipalityTax + vat;
	}

	return {
		subTotal: Number(subtotal.toFixed(2)),
		municipalityTax: Number(municipalityTax.toFixed(2)),
		calculatedVat: Number(vat.toFixed(2)),
		total: Number(total.toFixed(2)),
	};
};

export const checkIfRichTextIsEmpty = (richText: string) => {
	if (!richText) return true;
	try {
		const parsedText = JSON.parse(richText);
		return parsedText?.[0]?.children?.[0]?.text?.trim() === "";
	} catch (error) {
		return true;
	}
};

export const getBrandedQuoteHtmlContent = (
	quote: iBidType | ICorporateEventBid,
	userDetails: iUserDetails,
	isCorporateEvent?: boolean,
) => {
	return generateQuotePreviewHTML(
		{
			amount: quote?.amount,
			isMunicipalityTaxInclusive: quote?.isMunicipalityTaxInclusive ?? 0,
			municipalityTax: quote?.municipalityTax ?? 0,
			vat: quote?.vat ?? 0,
			isVatInclusive: quote?.isVatInclusive ?? 0,
			userQuoteAmount: 0,
		},
		{
			openAiFileId: quote?.openAiFileId ?? "",
			aiBidsExtractionLogId: quote?.aiBidsExtractionLogId ?? "",
			quoteUrl: quote?.quoteUrl ?? "",
			sections: quote?.sections ?? [],
		},
		userDetails,
		false,
		isCorporateEvent,
	);
};

export const calculateQuoteAmount = (bid: iBidType | ICorporateEventBid): string =>
	addCommasInAmount(bid ? (bid.amount ?? 0) + (bid.vat ?? 0) + (bid.municipalityTax ?? 0) : 0);

export const checkIfEverySectionHasAttachments = (
	sections: ICreateQuoteFormSection[],
	servicesInfoSection: ICreateQuoteFormSection | null,
) => {
	return (
		sections.every((section) => section.attachments?.length > 0) &&
		(servicesInfoSection?.attachments?.length as number) > 0
	);
};

export const checkIfAllSectionsHaveAttachments = (sections: ICreateQuoteFormSection[]) => {
	// All sections must have at least one attachment other then services info section
	return sections.every((section) => (section.attachments?.length ?? 0) > 0);
};

export const getUserQuotedAmount = (quote: iBidType | ICorporateEventBid) => {
	return (
		quote?.amount +
		(quote.isMunicipalityTaxInclusive === MUNICIPALITY_TAX_INCLUSIVE_STATUSES.Yes
			? quote?.municipalityTax
			: 0) +
		(quote.isVatInclusive === VAT_INCLUSIVE_STATUSES.Yes ? quote?.vat : 0)
	);
};
